/*
* (C) Behaviour Interactive Inc. - All Rights Reserved
* Unauthorized copying of this file, via any medium, is strictly prohibited
* This file is proprietary and confidential
*/

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

import Error from '@mui/icons-material/Error';

import moment from 'moment';
import { MergedAccount } from '../../../authentication/Authentication.model';
import { RequestError } from '../../../Error.model';
import { post } from '../../../utils/Request';
import { useSnackbar } from '../../../snackbar/SnackbarContext';
import { useConfig } from '../../../ConfigContext';

import { GameProviderConfig } from '../../../Config.model';
import { ProviderErrorModal } from './ProviderErrorModal';
import { ProviderConfirmationModal } from './ProviderConfirmationModal';
import { formatDateTime } from '../../../utils/Time';
import { useAuth } from '../../../authentication/AuthenticationContext';
import { getProviderLogo } from '../ProviderLogo';
import { ProviderMergeWarning } from './ProviderMergeWarning';

interface Props {
  provider: GameProviderConfig;
  gameId: string;
  mergedAccount?: MergedAccount;
  getMergedAccounts: () => Promise<void>;
}

export function ProviderMerge({
  provider,
  gameId,
  mergedAccount,
  getMergedAccounts,
}: Props): JSX.Element {
  const { t, i18n } = useTranslation(['translation', 'error']);
  const snackbar = useSnackbar();
  const config = useConfig();
  const auth = useAuth();

  const [showCustomErrorModal, setShowCustomErrorModal] = useState(false);
  const [showUnmergeModal, setShowUnmergeModal] = useState(false);
  const [isIntervalStarted, setIsIntervalStarted] = useState<boolean>(false);
  const [error, setError] = useState<RequestError>(null);

  const status = ['unknown', undefined].includes(mergedAccount?.status) ? 'unmerged' : mergedAccount?.status;
  const showUnmerge = status === 'merged' || (status === 'failed' && mergedAccount?.operation === 'unmerge');
  const providerLinked = auth.user.accounts.some((acc) => acc.type === provider.name);
  const gameOwned = auth.user.gameLibrary[gameId]?.providers.includes(provider.name);
  const featureDisabled = provider.killswitch.includes(showUnmerge ? 'unmerge' : 'merge');
  const disabled = featureDisabled || !providerLinked || !gameOwned;
  const isLoading = status === 'inProgress' || isIntervalStarted;
  const statusUnknown = status === 'failed' && mergedAccount?.operation === 'unknown';
  const closeCustomErrorModal = (): void => {
    setShowCustomErrorModal(false);
  };

  const mergeAction = async (action: 'merge' | 'unmerge'): Promise<void> => {
    try {
      await post(`${config.clientConfig.host}/players/me/${action}`, {
        data: {
          providerName: provider.name,
          gameId,
        },
      });
      await getMergedAccounts();
      setError(null);
    } catch (e) {
      const err = e as RequestError;
      setError(err);

      if (err.code === 'OldDataVersionError') {
        setShowCustomErrorModal(true);
      } else if (err.code === 'GameAccountMergeCooldownStillProcessingError') {
        const date = err.data?.date;
        if (date) {
          const locale = i18n.language;
          err.data.date = moment(date as string, 'x').isValid()
            ? formatDateTime(date as string, 'x', locale)
            : formatDateTime(date as string, 'DD-MM-YYYY HH:mm:ss', locale);
        }
        snackbar.addErrorMessage(err.code, err.data);
      } else {
        snackbar.addErrorMessage(err.code, err.data);
      }
    }
  };

  const openUnmergeModal = (): void => {
    setShowUnmergeModal(true);
  };
  const closeUnmergeModal = (): void => setShowUnmergeModal(false);

  const confirmUnmergeModal = (): void => {
    mergeAction('unmerge');
    closeUnmergeModal();
  };

  useEffect(() => {
    if (mergedAccount?.status === 'failed') {
      snackbar.addErrorMessage(mergedAccount.errorCode || 'UnknownError');
    }
  }, [mergedAccount]);

  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    const getPlayerMergedAccountsTimeout = (): void => {
      interval = setInterval(() => {
        getMergedAccounts();
      }, 3000);
    };

    if (mergedAccount?.status === 'inProgress' && !isIntervalStarted) {
      setIsIntervalStarted(true);
      getPlayerMergedAccountsTimeout();
    }
    if (mergedAccount?.status !== 'inProgress' && isIntervalStarted) {
      clearInterval(interval);
      setIsIntervalStarted(false);
    }

    return () => clearInterval(interval);
  }, [mergedAccount?.status]);

  return (
    <div className='provider-card text-center'>
      <img src={getProviderLogo(provider.name)} alt={provider.name} height='48' />
      <h4 className='mb-3'>{t(`translation:${provider.name}`)}</h4>

      <div
        className='status-badge mx-auto'
        data-cy={`${provider.name}-merge-status-text`}
      >
        {t(`translation:account.my-account.${error ? 'failed' : status}`)}
        {(status === 'failed' || error) ? (
          <Error fontSize='small' className='failed' />
        ) : (
          <div className={`status ${status}`} />
        )}
      </div>
      <button
        type='button'
        disabled={isLoading || disabled || statusUnknown}
        onClick={() => (showUnmerge ? openUnmergeModal() : mergeAction('merge'))}
        data-cy={`${provider.name}-${showUnmerge ? 'unmerge' : 'merge'}-button`}
        className={`btn btn-block mx-auto mt-3 mb-0
            ${showUnmerge ? 'btn-secondary' : 'btn-primary-game'}`}
      >
        {isLoading ? (
          <div className='p-2' style={{ display: 'flex', gap: '0.25rem' }}>
            {Array.from({ length: 3 }).map((_, i) => (
              <motion.div
                key={`${provider.name}-loading-${i}`}
                style={{ width: 8, height: 8, backgroundColor: 'white', borderRadius: '50%' }}
                animate={{ y: [-3, 3, -3] }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  delay: i * 0.1,
                  ease: 'easeInOut',
                }}
              />
            ))}
          </div>
        ) : (
          t(`translation:account.my-account.${showUnmerge ? 'unmerge' : 'merge'}`)
        )}
      </button>

      {disabled && (
        <ProviderMergeWarning
          featureDisabled={featureDisabled}
          providerLinked={providerLinked}
          gameOwned={gameOwned}
          provider={provider.name}
        />
      )}

      <ProviderConfirmationModal
        close={closeUnmergeModal}
        confirm={confirmUnmergeModal}
        show={showUnmergeModal}
        message={t('translation:account.provider.confirm-unmerge', { limit: '60', provider: t(provider.name) })}
      />

      <ProviderErrorModal
        close={closeCustomErrorModal}
        isOpen={showCustomErrorModal}
        error={error}
        provider={provider.name}
      />
    </div>
  );
}
